import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCalendarPlus, 
    faBuilding, 
    faMapMarkerAlt,
    faEdit,
    faTrash,
    faTimes,
    faClock,
    faUsers,
    faNoteSticky,
    faExclamationTriangle,
    faTruck,
    faUser,
    faBolt,
    faShieldAlt,
    faCalendarCheck
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { getNodeApiUrl } from '../../config/api.config';
import { useSelector } from 'react-redux';
import { selectAccountData } from '../../redux/main/accountSlice';
import moment from 'moment';
import 'moment/locale/it';

moment.locale('it');

const GenericAppointmentDetailsModal = ({ 
    isOpen, 
    toggle, 
    appointment, 
    onEdit, 
    onDelete 
}) => {
    const [systemDetails, setSystemDetails] = useState(null);
    const [loadingSystem, setLoadingSystem] = useState(false);
    const accountData = useSelector(selectAccountData);
    
    // Carica dettagli impianto quando si apre la modale
    useEffect(() => {
        const fetchSystemDetails = async () => {
            if (!isOpen || !appointment?.systemId || !accountData?._id) {
                setSystemDetails(null);
                return;
            }
            
            setLoadingSystem(true);
            try {
                const token = localStorage.getItem('installamelo-auth-token');
                const response = await axios.get(
                    getNodeApiUrl('systems'),
                    {
                        headers: { 'auth-token': token },
                        params: { 
                            systemId: appointment.systemId,
                            accountId: accountData._id 
                        }
                    }
                );
                setSystemDetails(response.data);
            } catch (error) {
                console.error('Errore caricamento dettagli impianto:', error);
                setSystemDetails(null);
            } finally {
                setLoadingSystem(false);
            }
        };
        
        fetchSystemDetails();
    }, [isOpen, appointment?.systemId, accountData?._id]);
    
    if (!appointment) return null;

    // Calcola durata
    const calculateDuration = () => {
        if (appointment.duration) {
            const hours = Math.floor(appointment.duration / 60);
            const minutes = appointment.duration % 60;
            return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
        }
        if (appointment.fromDate && appointment.toDate) {
            const from = moment(appointment.fromDate);
            const to = moment(appointment.toDate);
            const diffMinutes = to.diff(from, 'minutes');
            const hours = Math.floor(diffMinutes / 60);
            const minutes = diffMinutes % 60;
            return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
        }
        return 'N/D';
    };

    // Formatta data e ora
    const formatDateTime = (date) => {
        if (!date) return 'N/D';
        return moment(date).format('DD MMM YYYY, HH:mm');
    };

    // Formatta solo la data
    const formatDate = (date) => {
        if (!date) return 'N/D';
        return moment(date).format('DD/MM/YYYY');
    };

    // Formatta solo l'ora
    const formatTime = (date) => {
        if (!date) return 'N/D';
        return moment(date).format('HH:mm');
    };

    // Gestione click elimina - apre direttamente la modale di eliminazione con notifiche
    const handleDeleteClick = () => {
        onDelete && onDelete(appointment);
    };

    // Colore primary del tema
    const primaryColor = 'rgb(56, 56, 56)';

    // Componente per le sezioni
    const Section = ({ icon, title, children, className = '' }) => (
        <div className={`mb-3 ${className}`}>
            <div className="d-flex align-items-center mb-2">
                <FontAwesomeIcon icon={icon} className="me-2" style={{ width: '16px', color: primaryColor }} />
                <span className="fw-semibold text-secondary" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {title}
                </span>
            </div>
            <div className="ps-4">
                {children}
            </div>
        </div>
    );

    // Componente per i tag utenti
    const UserTag = ({ user }) => (
        <div 
            className="d-flex align-items-center mb-2 px-3 py-2 rounded"
            style={{ 
                backgroundColor: user.userColor ? `${user.userColor}15` : '#f8f9fa',
                border: `1px solid ${user.userColor || '#dee2e6'}`,
            }}
        >
            <span 
                className="rounded-circle me-2"
                style={{ 
                    width: '10px', 
                    height: '10px', 
                    backgroundColor: user.userColor || '#2196f3',
                    display: 'inline-block'
                }}
            />
            <span className="fw-medium" style={{ fontSize: '0.9rem' }}>{user.userLabel}</span>
        </div>
    );

    // Verifica se l'appuntamento è su più giorni
    const isMultiDay = appointment.fromDate && appointment.toDate && 
        !moment(appointment.fromDate).isSame(moment(appointment.toDate), 'day');

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} centered size="md">
                <ModalHeader toggle={toggle} className="border-0 pb-0">
                    <div className="d-flex align-items-center">
                        <div 
                            className="rounded-circle me-2 d-flex align-items-center justify-content-center"
                            style={{ 
                                width: '36px', 
                                height: '36px', 
                                backgroundColor: appointment.color || '#7367f0'
                            }}
                        >
                            <FontAwesomeIcon icon={faCalendarPlus} className="text-white" />
                        </div>
                        <span>Dettagli Appuntamento</span>
                    </div>
                </ModalHeader>
                
                <ModalBody className="pt-3">
                    {/* Header con titolo e badge */}
                    <div className="mb-4 pb-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <h5 className="mb-1 fw-bold">
                                    {appointment.title || 'Appuntamento Generico'}
                                </h5>
                                <Badge color="warning" className="px-2 py-1">
                                    Appuntamento Generico
                                </Badge>
                            </div>
                            <div 
                                className="rounded px-2 py-1" 
                                style={{ backgroundColor: appointment.color || '#7367f0', minWidth: '40px' }}
                            >
                                <span className="text-white small">&nbsp;</span>
                            </div>
                        </div>
                    </div>

                    {/* Data e Ora */}
                    <Section icon={faClock} title="Data e Ora">
                        <div className="row g-2">
                            <div className="col-6">
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Data Inizio</div>
                                <div className="fw-medium">{formatDate(appointment.fromDate)}</div>
                            </div>
                            <div className="col-6">
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Data Fine</div>
                                <div className="fw-medium">{formatDate(appointment.toDate)}</div>
                            </div>
                            <div className="col-6">
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Ora Inizio</div>
                                <div className="fw-medium">{formatTime(appointment.fromDate)}</div>
                            </div>
                            <div className="col-6">
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Ora Fine</div>
                                <div className="fw-medium">{formatTime(appointment.toDate)}</div>
                            </div>
                            <div className="col-12">
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Durata</div>
                                <div className="fw-medium">{calculateDuration()}</div>
                            </div>
                        </div>
                    </Section>

                    {/* Collaboratori */}
                    {appointment.users && appointment.users.length > 0 && (
                        <Section icon={faUsers} title={`Collaboratori (${appointment.users.length})`}>
                            {appointment.users.map((user, idx) => (
                                <UserTag key={idx} user={user} />
                            ))}
                        </Section>
                    )}

                    {/* Cliente */}
                    {appointment.customerLabel && (
                        <Section icon={faUser} title="Cliente">
                            <div className="fw-medium">{appointment.customerLabel}</div>
                        </Section>
                    )}

                    {/* Impianto */}
                    {(appointment.systemLabel || appointment.systemId) && (
                        <Section icon={faBuilding} title="Impianto">
                            {loadingSystem ? (
                                <div className="d-flex align-items-center text-muted">
                                    <Spinner size="sm" className="me-2" />
                                    Caricamento dettagli impianto...
                                </div>
                            ) : systemDetails ? (
                                <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                    {/* Nome impianto */}
                                    <div className="fw-bold mb-2" style={{ fontSize: '1rem' }}>
                                        {systemDetails.title || appointment.systemLabel}
                                    </div>
                                    
                                    {/* Indirizzo */}
                                    {systemDetails.customerAddressLabel && (
                                        <div className="d-flex align-items-start mb-3">
                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 mt-1 text-secondary" style={{ width: '14px' }} />
                                            <span style={{ fontSize: '0.9rem' }}>{systemDetails.customerAddressLabel}</span>
                                        </div>
                                    )}
                                    
                                    {/* Tipi di impianto */}
                                    {systemDetails.typesList && systemDetails.typesList.length > 0 && (
                                        <div>
                                            <div className="text-muted mb-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                <FontAwesomeIcon icon={faBolt} className="me-1" />
                                                Tipi di Impianto
                                            </div>
                                            {systemDetails.typesList.map((type, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className="d-flex align-items-center justify-content-between mb-2 p-2 rounded"
                                                    style={{ backgroundColor: '#fff', border: '1px solid #dee2e6' }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <span className="fw-medium">{type.typeLabel}</span>
                                                    </div>
                                                    {type.expirationWarrantyDate && (
                                                        <div className="d-flex align-items-center">
                                                            <FontAwesomeIcon 
                                                                icon={faShieldAlt} 
                                                                className={`me-1 ${moment(type.expirationWarrantyDate).isBefore(moment()) ? 'text-danger' : 'text-success'}`} 
                                                                style={{ width: '12px' }}
                                                            />
                                                            <small className={moment(type.expirationWarrantyDate).isBefore(moment()) ? 'text-danger' : 'text-success'}>
                                                                Garanzia: {moment(type.expirationWarrantyDate).format('DD/MM/YYYY')}
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Data ultimo test se disponibile */}
                                    {systemDetails.typesList && systemDetails.typesList.some(t => t.testedDate) && (
                                        <div className="mt-2 pt-2 border-top">
                                            {systemDetails.typesList.filter(t => t.testedDate).map((type, idx) => (
                                                <div key={idx} className="d-flex align-items-center text-muted" style={{ fontSize: '0.8rem' }}>
                                                    <FontAwesomeIcon icon={faCalendarCheck} className="me-1" style={{ width: '12px' }} />
                                                    <span>{type.typeLabel}: ultimo test {moment(type.testedDate).format('DD/MM/YYYY')}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <div className="fw-medium">{appointment.systemLabel}</div>
                                    {appointment.systemAddressLabel && (
                                        <div className="text-muted mt-1" style={{ fontSize: '0.85rem' }}>
                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                            {appointment.systemAddressLabel}
                                        </div>
                                    )}
                                </div>
                            )}
                        </Section>
                    )}

                    {/* Fornitore */}
                    {appointment.supplierLabel && (
                        <Section icon={faTruck} title="Fornitore">
                            <div className="fw-medium">{appointment.supplierLabel}</div>
                        </Section>
                    )}

                    {/* Note/Descrizione */}
                    {appointment.notes && (
                        <Section icon={faNoteSticky} title="Descrizione">
                            <div className="p-2 bg-light rounded" style={{ fontSize: '0.9rem' }}>
                                {appointment.notes}
                            </div>
                        </Section>
                    )}

                    {/* Info creazione */}
                    <div className="mt-3 pt-3 border-top">
                        <small className="text-muted">
                            Creato il {formatDateTime(appointment.createDate)}
                        </small>
                    </div>
                </ModalBody>
                
                <ModalFooter className="border-0 pt-0">
                    <Button color="secondary" outline onClick={toggle}>
                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                        Chiudi
                    </Button>
                    <Button color="danger" outline onClick={handleDeleteClick}>
                        <FontAwesomeIcon icon={faTrash} className="me-1" />
                        Elimina
                    </Button>
                    <Button color="primary" onClick={() => onEdit && onEdit(appointment)}>
                        <FontAwesomeIcon icon={faEdit} className="me-1" />
                        Modifica
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default GenericAppointmentDetailsModal;
